import { useMemo, useState } from 'react';

import { SapaBankStatus } from '@/components/Shared/constants/banks-statuses';
import { BANKS_ICONS_NAMES, DictTypes } from '@/constants';
import { useWindowSize } from '@/hooks/useWindowSize';
import { safeKey } from '@/services/helpers/dict';
import { useAppSelector } from '@/store';

import type { TBankOffers } from '../../../types/bank-offer.types';
import useAdditionalHelpers from './useAdditionalHelpers';

interface IProps {
  bankOffer: TBankOffers;
  hasOfferedResponse?: boolean;
}

const blueColoredStatuses = [SapaBankStatus.WaitingOfFinancing];

const useFormulateOfferItem = ({
  bankOffer: initialBankOffer,
  hasOfferedResponse
}: IProps) => {
  const [windowWidth] = useWindowSize();
  const isMobile = windowWidth <= 860;

  const { dicts } = useAppSelector((state) => state.dict);
  const { orderApp } = useAppSelector((state) => state.newOrder);

  const [offerDetailsModal, setOfferDetailsModal] = useState<boolean | string>(
    false
  );

  const bankOffer: TBankOffers = useMemo(() => {
    const hasAlternatives = Boolean(
      initialBankOffer.alternatives && initialBankOffer.alternatives.length > 0
    );
    const product =
      orderApp?.productInfo.product.stocksBonds?.find(
        (prod) => prod.bank_name === initialBankOffer.bank.name
      )?.label || 'Стандарт';

    if (!hasAlternatives) {
      return {
        ...initialBankOffer,
        product
      };
    }

    const hasMainInfo =
      initialBankOffer.monthlyPayment && initialBankOffer.rate;

    const alternatives = initialBankOffer.alternatives || [];
    const selectedAlternative =
      alternatives.find(
        (item) => item.extUuid === initialBankOffer.offeredUuid
      ) || (!hasMainInfo ? alternatives[0] : undefined);
    const filteredAlternatives = alternatives.filter(
      (item) => item.extUuid !== selectedAlternative?.extUuid
    );

    let result = {
      ...initialBankOffer,
      alternatives: filteredAlternatives,
      product
    };
    if (selectedAlternative) {
      result = {
        ...result,
        rate: selectedAlternative.rate.toString(),
        extId: selectedAlternative.extUuid,
        initialPayment: selectedAlternative.initialPayment,
        loanAmount: selectedAlternative.loanAmount,
        loanPeriod: selectedAlternative.loanPeriod.toString(),
        monthlyPayment: selectedAlternative.monthlyPayment,
        product: selectedAlternative.product || product
      };
    }

    return result as TBankOffers;
  }, [initialBankOffer, orderApp?.productInfo.product.stocksBonds]);

  const { additionalProducts, getDescription } = useAdditionalHelpers({
    bankOffer: bankOffer,
    offerDetailsModal
  });

  const bankDict =
    dicts?.[DictTypes.Banks]?.getBy.name[safeKey(bankOffer.bank.name)];
  const bankIconName =
    BANKS_ICONS_NAMES[
      initialBankOffer.bank.name as keyof typeof BANKS_ICONS_NAMES
    ];
  const isCurrentResponseOffered =
    hasOfferedResponse && initialBankOffer.isOffered;

  return {
    additionalProducts,
    bankOffer,
    getDescription,
    headerIcon: bankDict?.icon || bankIconName?.icon,
    headerTitle: bankDict?.fullname || bankIconName?.name,
    isBlueColoredStatus: blueColoredStatuses.includes(
      initialBankOffer.status.code as SapaBankStatus
    ),
    isCurrentResponseOffered,
    isMobile,
    offerDetailsModal,
    setOfferDetailsModal
  };
};

export default useFormulateOfferItem;
